import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  LinkIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  TrophyIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { ScrollAnimation } from '../components/ScrollAnimation';
import TechnologyPartners from '../components/TechnologyPartners';
import ContentBreak from '../components/ContentBreak';
import { useMetrics } from '../hooks/useMetrics';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  bio: string;
  expertise: string[];
  linkedin?: string;
  email?: string;
  image?: string;
  imagePosition?: string;
  imageScale?: string;
}

// Founders and leadership team
const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: 'Ian Strom',
    role: 'Managing Partner & Co-Founder',
    bio: 'Ian Strom brings 14+ years of technology leadership to Davinci, evolving from hands-on IT infrastructure to C-suite strategy. As Managing Partner since 2018, he champions AI-driven transformation while maintaining a simple principle: technology should amplify human capability, not complicate it. A serial entrepreneur with ventures spanning multiple industries, Ian approaches business challenges as opportunities to build better systems for human growth.',
    expertise: ['AI Strategy', 'Business Transformation', 'Technology Leadership', 'Entrepreneurship'],
    linkedin: 'https://www.linkedin.com/in/istrom/',
    image: '/static/images/team/ian-strom-centered.jpg'
  },
  {
    id: 2,
    name: 'Ryan Hulsker',
    role: 'Partner & Co-Founder',
    bio: "Ryan Hulsker combines deep infrastructure expertise with innovative, cross-disciplinary thinking as one of Davinci's partners and co-founder. From his early work on foundational cloud systems to architecting enterprise transformations, he brings a unique ability to simplify the complex. Ryan's approach—connecting ideas from diverse fields to create game-changing solutions—defines Davinci's technical vision.",
    expertise: ['Cloud Architecture', 'Infrastructure', 'Enterprise Systems', 'Technical Innovation'],
    linkedin: 'https://www.linkedin.com/in/ryan-hulsker-73504a1b6/',
    image: '/static/images/team/ryan-hulsker-centered.jpg'
  }
];

// Animations
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  padding: var(--spacing-section-mobile) 0;
  background: linear-gradient(180deg, 
    var(--color-primary-light) 0%, 
    var(--color-primary-base) 30%,
    var(--color-primary-light) 70%,
    var(--color-primary-base) 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: var(--spacing-section) 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 80%, rgba(11,152,206,0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(49,110,168,0.04) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: var(--spacing-4xl);
  position: relative;
  z-index: 2;
`;

const AboutBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-md);
  background: rgba(11,152,206,0.1);
  border: 1px solid rgba(11,152,206,0.2);
  border-radius: 999px;
  color: var(--color-accent-primary);
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--spacing-xl);
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h1`
  font-size: var(--fs-5xl);
  background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: var(--spacing-lg);
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: var(--fs-6xl);
  }
`;

const Subtitle = styled.p`
  font-size: var(--fs-xl);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: var(--lh-relaxed);
`;

const StatsSection = styled.div`
  display: inline-flex;
  gap: var(--spacing-xl);
  padding: var(--spacing-md) var(--spacing-xl);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 999px;
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(11,152,206,0.1);
  margin-top: var(--spacing-lg);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  cursor: default;
  
  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 15px 40px rgba(11, 152, 206, 0.2);
    border-color: rgba(11, 152, 206, 0.25);
    background: rgba(255, 255, 255, 0.95);
  }
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: var(--spacing-sm);
    border-radius: 24px;
  }
`;

const StatCard = styled.div`
  text-align: center;
  
  .stat-value {
    font-size: var(--fs-xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-accent-primary);
    margin-bottom: var(--spacing-xs);
    display: block;
    transition: transform 0.3s ease;
  }
  
  .stat-label {
    font-size: var(--fs-xs);
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: var(--ls-wider);
  }
  
  ${StatsSection}:hover & .stat-value {
    transform: scale(1.1);
  }
`;

const MissionSection = styled.section`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(11,152,206,0.1);
  color: var(--text-primary);
  padding: var(--spacing-4xl);
  border-radius: 24px;
  margin-bottom: var(--spacing-4xl);
  margin-top: var(--spacing-3xl);
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
  position: relative;
  z-index: 2;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, background 0.3s ease;
  cursor: default;
  
  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 15px 40px rgba(11, 152, 206, 0.2);
    border-color: rgba(11, 152, 206, 0.25);
    background: rgba(255, 255, 255, 0.98);
    
    h2 {
      transform: scale(1.05);
    }
  }

  h2 {
    margin-bottom: var(--spacing-lg);
    font-size: var(--fs-3xl);
    color: var(--color-accent-primary);
    position: relative;
    z-index: 1;
    text-align: center;
    transition: transform 0.3s ease;
  }

  .mission-content {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--spacing-3xl);
    align-items: center;
    max-width: 1200px;
    margin: 0 auto;
    
    @media (min-width: 1024px) {
      grid-template-columns: 1fr 1fr;
      gap: var(--spacing-4xl);
    }
  }

  .mission-text {
    font-size: var(--fs-lg);
    line-height: var(--lh-relaxed);
    color: var(--text-secondary);
    position: relative;
    z-index: 1;
    
    @media (min-width: 1024px) {
      text-align: left;
    }
  }

  .mission-video {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
    background: #000;
    border: 2px solid rgba(11, 152, 206, 0.1);
    
    @media (max-width: 1023px) {
      max-width: 600px;
      margin: 0 auto;
    }
    
    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
    }
  }
`;

const TeamSection = styled.section`
  margin-bottom: var(--spacing-4xl);
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  color: var(--text-primary);
  margin-bottom: var(--spacing-xl);
  text-align: center;
  font-size: var(--fs-3xl);
`;

const SectionIntro = styled.p`
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--fs-lg);
  max-width: 700px;
  margin: 0 auto var(--spacing-3xl);
  line-height: var(--lh-relaxed);
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-3xl);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-2xl);
  }
`;

const TeamCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(11,152,206,0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  ${css`animation: ${fadeIn} 0.6s ease-out;`}

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, 
      var(--color-accent-primary), 
      var(--color-accent-secondary),
      var(--color-accent-primary));
    background-size: 200% 200%;
    ${css`animation: ${gradientMove} 8s ease infinite;`}
    border-radius: 24px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 25px 80px rgba(11,152,206,0.2);
    
    &::before {
      opacity: 0.3;
    }
    
    .member-avatar {
      ${css`animation: ${float} 2s ease-in-out infinite;`}
      transform: scale(1.05);
    }
  }
`;

const MemberHeader = styled.div`
  display: flex;
  gap: var(--spacing-lg);
  padding: var(--spacing-xl);
  align-items: center;
  
  @media (max-width: 767px) {
    flex-direction: column;
    text-align: center;
  }
`;

const MemberAvatar = styled.div<{ $backgroundImage?: string; $imagePosition?: string; $imageScale?: string }>`
  width: 120px;
  height: 120px;
  min-width: 120px;
  border-radius: 50%;
  background: ${props => props.$backgroundImage 
    ? `url(${props.$backgroundImage})` 
    : 'linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary))'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-3xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-inverse);
  transition: transform 0.3s ease;
  overflow: hidden;
  
  @media (max-width: 767px) {
    width: 100px;
    height: 100px;
    min-width: 100px;
  }
`;

const MemberInfo = styled.div`
  flex: 1;
  
  h3 {
    color: var(--text-primary);
    font-size: var(--fs-xl);
    margin-bottom: var(--spacing-xs);
  }
  
  .role {
    color: var(--color-accent-primary);
    font-size: var(--fs-md);
    font-weight: var(--font-weight-medium);
    margin-bottom: var(--spacing-sm);
  }
  
  .contact {
    display: flex;
    gap: var(--spacing-sm);
    
    @media (max-width: 767px) {
      justify-content: center;
    }
    
    a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--color-primary-light);
      color: var(--text-secondary);
      transition: all 0.2s ease;
      
      &:hover {
        background: var(--color-accent-primary);
        color: var(--text-inverse);
        transform: translateY(-2px);
      }
      
      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

const MemberBody = styled.div`
  padding: 0 var(--spacing-xl) var(--spacing-xl);
  
  .bio {
    color: var(--text-secondary);
    line-height: var(--lh-relaxed);
    margin-bottom: var(--spacing-lg);
  }
  
  .expertise {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-xs);
    
    span {
      padding: var(--spacing-xs) var(--spacing-sm);
      background: var(--color-primary-light);
      border: 1px solid var(--border-light);
      border-radius: 20px;
      font-size: var(--fs-sm);
      color: var(--text-secondary);
    }
  }
`;

const ContentSection = styled.section`
  margin-bottom: var(--spacing-4xl);
  position: relative;
  z-index: 2;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-2xl);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    align-items: stretch;
  }
`;

const ContentCard = styled.div<{ $backgroundImage?: string }>`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  padding: var(--spacing-2xl);
  border-radius: 20px;
  border: 1px solid rgba(11,152,206,0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: hidden;
  ${css`animation: ${fadeIn} 0.6s ease-out;`}

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, 
      var(--color-accent-primary), 
      var(--color-accent-secondary),
      var(--color-accent-primary));
    background-size: 200% 200%;
    ${css`animation: ${gradientMove} 8s ease infinite;`}
    border-radius: 20px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.$backgroundImage ? `url('${props.$backgroundImage}') center/cover no-repeat` : 'none'};
    opacity: ${props => props.$backgroundImage ? 'var(--bg-opacity, 0.08)' : '0'};
    border-radius: 20px;
    z-index: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-10px) scale(1.03) rotateX(2deg);
    box-shadow: 0 25px 60px rgba(11,152,206,0.2);
    border-color: rgba(11,152,206,0.4);
    
    &::before {
      opacity: 0.08;
    }
    
    &::after {
      opacity: ${props => props.$backgroundImage ? 'var(--bg-hover-opacity, 0.15)' : '0'};
    }
    
    .icon-wrapper {
      ${css`animation: ${float} 2s ease-in-out infinite;`}
      transform: scale(1.15) rotate(-5deg);
    }
  }

  .icon-wrapper {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--spacing-lg);
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(11,152,206,0.3);
    position: relative;
    z-index: 1;
    
    svg {
      width: 32px;
      height: 32px;
      color: white;
    }
  }

  h3 {
    color: var(--text-primary);
    margin-bottom: var(--spacing-md);
    font-size: var(--fs-xl);
    font-weight: var(--font-weight-bold);
    position: relative;
    z-index: 1;
  }

  p {
    color: var(--text-secondary);
    line-height: var(--lh-relaxed);
    flex: 1;
    position: relative;
    z-index: 1;
  }
`;

// PartnersSection moved to TechnologyPartners component

const PartnerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--spacing-md);
  margin-top: var(--spacing-2xl);
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: var(--spacing-md);
  }
`;

// Partner carousel styles moved to TechnologyPartners component
// Keeping legacy styled components for reference
const PartnerCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: var(--spacing-md) 0;
  margin-top: var(--spacing-xl);

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 56px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, var(--color-primary-light), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(270deg, var(--color-primary-light), transparent);
  }
`;

const LegacyPartnerCarouselTrack = styled.div`
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
  width: fit-content;
  animation: partners-scroll 100s linear infinite;

  &:hover { animation-play-state: paused; }

  @keyframes partners-scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

const LegacyPartnerCard = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 12px;
  min-height: 72px;
  padding: var(--spacing-md);
  box-shadow: none;
  transition: all var(--transition-base);
  text-decoration: none;
  color: inherit;
  filter: grayscale(100%);
  isolation: isolate;
  overflow: hidden;
  opacity: 0.85;

  img {
    height: 64px;   /* uniform visual height */
    width: auto;    /* allow width to vary by aspect */
    object-fit: contain;
    mix-blend-mode: multiply;
    background: transparent;
    max-width: 160px; /* safety cap */
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: none;
    border-color: transparent;
    filter: grayscale(0%);
    opacity: 1;
  }

  @media (min-width: 768px) {
    min-height: 84px;

    img { height: 72px; width: auto; max-width: 180px; }
  }
`;

const PartnerLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  padding: var(--spacing-lg);
  opacity: 0.8;
  transition: all var(--transition-base);
  cursor: pointer;
  filter: grayscale(100%);
  isolation: isolate;
  
  img {
    max-width: 150px;
    max-height: 60px;
    width: auto;
    height: auto;
    object-fit: contain;
    mix-blend-mode: multiply;
    background: transparent;
  }
  
  &:hover {
    opacity: 1;
    transform: translateY(-4px);
    filter: grayscale(0%);
  }
  
  @media (min-width: 768px) {
    height: 120px;
    
    img {
      max-width: 180px;
      max-height: 80px;
    }
  }
`;

const About: React.FC = () => {
  const { metrics } = useMetrics();
  
  // Partner data moved to TechnologyPartners component
  return (
    <PageWrapper>
      <div className="container">
        <HeroSection>
          <ScrollAnimation animation="fadeIn" delay={100}>
            <AboutBadge>
              <SparklesIcon />
              Workflow Superhero Specialists
            </AboutBadge>
            <Title>Alberta's AI Implementation Specialists</Title>
            <Subtitle>
              Universities research AI. Big tech builds AI models. Consultants theorize about AI. 
              We IMPLEMENT AI. In production. With real data. Solving actual problems. 
              That's the difference between a paper and a payoff.
            </Subtitle>
            
            <StatsSection>
              <StatCard>
                <span className="stat-value">{metrics?.active_clients_display || '25+'}</span>
                <span className="stat-label">Active Clients</span>
              </StatCard>
              <StatCard>
                <span className="stat-value">7+</span>
                <span className="stat-label">Industries</span>
              </StatCard>
              <StatCard>
                <span className="stat-value">24/7</span>
                <span className="stat-label">Support</span>
              </StatCard>
            </StatsSection>
          </ScrollAnimation>
        </HeroSection>

        <ScrollAnimation animation="fadeIn" delay={100}>
          <MissionSection>
            <h2>We're Implementers, Not Researchers</h2>
            <div className="mission-content">
              <div className="mission-text">
                <p>
                  We started as an IT company and learned something crucial: AI implementation is harder 
                  than people think. It's not just about connecting to GPT-4 - it's about preventing 
                  hallucinations, providing structured answers, integrating with existing tools without 
                  rebuilding everything, and giving AI safe access to your data. We've mastered the nuance 
                  that makes the difference between a demo and a production-ready solution.
                </p>
              </div>
              <div className="mission-video">
                <iframe 
                  src="https://www.youtube.com/embed/V5ZyxdUAimc?rel=0&modestbranding=1&autoplay=0&controls=1"
                  title="Davinci AI Solutions Company Overview"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </MissionSection>
        </ScrollAnimation>

        <ContentBreak variant="gradient" spacing="large" />

        <TeamSection>
          <ScrollAnimation animation="slideInFromBottom" delay={100}>
            <SectionTitle>Meet Our Leadership Team</SectionTitle>
            <SectionIntro>
              Passionate experts dedicated to your AI transformation success
            </SectionIntro>
          </ScrollAnimation>
          
          <TeamGrid>
            {teamMembers.map((member, index) => (
              <ScrollAnimation 
                key={member.id}
                animation="scaleIn"
                delay={200 + index * 100}
              >
                <TeamCard>
                  <MemberHeader>
                    <MemberAvatar 
                      className="member-avatar" 
                      $backgroundImage={member.image}
                      $imagePosition={member.imagePosition}
                      $imageScale={member.imageScale}
                    >
                      {!member.image && member.name.split(' ').map(n => n[0]).join('')}
                    </MemberAvatar>
                    <MemberInfo>
                      <h3>{member.name}</h3>
                      <div className="role">{member.role}</div>
                      <div className="contact">
                        {member.linkedin && (
                          <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                            <LinkIcon />
                          </a>
                        )}
                        {member.email && (
                          <a href={`mailto:${member.email}`}>
                            <EnvelopeIcon />
                          </a>
                        )}
                      </div>
                    </MemberInfo>
                  </MemberHeader>
                  <MemberBody>
                    <p className="bio">{member.bio}</p>
                    <div className="expertise">
                      {member.expertise.map((skill, i) => (
                        <span key={i}>{skill}</span>
                      ))}
                    </div>
                  </MemberBody>
                </TeamCard>
              </ScrollAnimation>
            ))}
          </TeamGrid>
        </TeamSection>

        <ContentBreak variant="gradient" spacing="large" />

        <ContentSection>
          <ScrollAnimation animation="slideInFromBottom" delay={100}>
            <SectionTitle>Why We're Alberta's AI Implementation Specialists</SectionTitle>
          </ScrollAnimation>
          <ContentGrid>
            <ScrollAnimation animation="fadeIn" delay={200}>
              <ContentCard $backgroundImage="/images/about/ai-first-approach.webp" style={{ '--bg-opacity': '0.06', '--bg-hover-opacity': '0.12' } as React.CSSProperties}>
                <div className="icon-wrapper">
                  <BriefcaseIcon />
                </div>
                <h3>Employee Augmentation, Not Replacement</h3>
                <p>
                  We design AI solutions to make your existing team superhuman—amplifying their capabilities 
                  rather than replacing them. Every implementation focuses on workflow augmentation that 
                  makes people more valuable, not obsolete.
                </p>
              </ContentCard>
            </ScrollAnimation>
            <ScrollAnimation animation="fadeIn" delay={300}>
              <ContentCard $backgroundImage="/images/about/vendor-neutral-design.webp" style={{ '--bg-opacity': '0.06', '--bg-hover-opacity': '0.12' } as React.CSSProperties}>
                <div className="icon-wrapper">
                  <AcademicCapIcon />
                </div>
                <h3>We Eat Our Own Dog Food</h3>
                <p>
                  We live and breathe AI every single day. Our own operations are a constant testing ground
                  where we push the limits, iterate relentlessly, and discover what truly works. We're not
                  just consultants who recommend AI - we're practitioners who depend on it, refine it, and
                  understand its real-world impact because we experience it ourselves daily.
                </p>
              </ContentCard>
            </ScrollAnimation>
            <ScrollAnimation animation="fadeIn" delay={400}>
              <ContentCard $backgroundImage="/images/about/proven-expertise.webp">
                <div className="icon-wrapper">
                  <TrophyIcon />
                </div>
                <h3>Proven Experience You Can Trust</h3>
                <p>
                  We've successfully implemented AI across 7+ industries. We're not guessing or theorizing -
                  we've done this before, many times. Our experience means we know what works, what doesn't,
                  and how to avoid the pitfalls that trap others. You're working with seasoned professionals
                  who deliver results, not promises.
                </p>
              </ContentCard>
            </ScrollAnimation>
            <ScrollAnimation animation="fadeIn" delay={500}>
              <ContentCard $backgroundImage="/images/about/strategic-partnership.webp">
                <div className="icon-wrapper">
                  <BriefcaseIcon />
                </div>
                <h3>End-to-End Partnership, Not Just Vendors</h3>
                <p>
                  We're your long-term partners through every phase: strategy, planning, development,
                  training, ongoing support, and continuous iteration. We don't disappear after deployment -
                  we stay engaged, optimize, and evolve with you. This isn't a transaction; it's a partnership
                  that grows stronger as your AI capabilities mature.
                </p>
              </ContentCard>
            </ScrollAnimation>
          </ContentGrid>
        </ContentSection>

        {/* Technology Partners Section - using shared component */}
        <ScrollAnimation animation="fadeIn" delay={100}>
          <TechnologyPartners />
        </ScrollAnimation>
      </div>
    </PageWrapper>
  );
};

export default About;
